import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import { useGoogleOneTapLogin, CredentialResponse } from '@react-oauth/google';
import { api } from '../api/config';
import { isKioskMode } from '../utils/kioskMode';

interface User {
  email: string;
  name: string;
  picture?: string;
  token: string;
  tokenId?: number;
}

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  login: (googleCredential: string) => Promise<void>;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const ALLOWED_DOMAIN = 'hexacontainment.com';

// E2E auth bypass — only fires when the build was explicitly configured for it.
// CRA inlines REACT_APP_* at build time, so production bundles physically cannot
// contain this branch. See docs/WIP/e2e-auth-bypass.md.
const E2E_LOGIN_ENABLED = process.env.REACT_APP_E2E_LOGIN === 'true';
const E2E_USER_EMAIL = process.env.REACT_APP_E2E_USER_EMAIL || 'qa@hexacontainment.com';

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// Client-side decode for display only — server verifies signature via Google::Auth::IDTokens
function decodeGoogleJwt(credential: string) {
  const base64Url = credential.split('.')[1];
  const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
  const jsonPayload = decodeURIComponent(
    atob(base64)
      .split('')
      .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
      .join('')
  );
  return JSON.parse(jsonPayload);
}

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const kiosk = isKioskMode();

  const [user, setUser] = useState<User | null>(() => {
    if (kiosk) {
      // Kiosk mode authenticates via X-Kiosk-Token on every API call. No real
      // user identity — surface a placeholder so isAuthenticated is true and
      // ProtectedRoute lets the dashboard render.
      return { email: 'kiosk', name: 'Kiosk', token: 'kiosk' };
    }
    if (E2E_LOGIN_ENABLED) {
      return { email: E2E_USER_EMAIL, name: 'E2E User', token: 'e2e' };
    }

    // Synchronously restore session from localStorage to avoid
    // flash-redirect to /login before useEffect can run
    const storedUser = localStorage.getItem('auth_user');
    if (storedUser) {
      try {
        const parsedUser = JSON.parse(storedUser);
        if (parsedUser.email && parsedUser.email.endsWith(`@${ALLOWED_DOMAIN}`) && parsedUser.token) {
          return parsedUser;
        }
        localStorage.removeItem('auth_user');
      } catch {
        localStorage.removeItem('auth_user');
      }
    }
    return null;
  });

  const login = useCallback(async (googleCredential: string) => {
    const payload = decodeGoogleJwt(googleCredential);
    const userEmail = payload.email;
    const userName = payload.name || payload.email;

    if (!userEmail || !userEmail.endsWith(`@${ALLOWED_DOMAIN}`)) {
      throw new Error(`Access restricted to @${ALLOWED_DOMAIN} email addresses only`);
    }

    // Exchange the short-lived Google ID token for a long-lived session token
    const { data } = await api.post('/session_tokens', { name: 'admin-spa' }, {
      headers: { Authorization: `Bearer ${googleCredential}` },
    });

    const userData: User = {
      email: userEmail,
      name: userName,
      picture: payload.picture,
      token: data.token,
      tokenId: data.id,
    };

    setUser(userData);
    localStorage.setItem('auth_user', JSON.stringify(userData));
  }, []);

  const logout = useCallback(() => {
    // Revoke the session token server-side (best-effort).
    // Capture token/id before clearing localStorage so the request is self-contained.
    const stored = localStorage.getItem('auth_user');
    setUser(null);
    localStorage.removeItem('auth_user');

    if (stored) {
      try {
        const { token, tokenId } = JSON.parse(stored);
        if (tokenId && token) {
          api.delete(`/session_tokens/${tokenId}`, {
            headers: { Authorization: `Bearer ${token}` },
          }).catch((err) => console.warn('Token revocation failed:', err));
        }
      } catch {
        // Ignore — we're logging out regardless
      }
    }
  }, []);

  // Handle Google One Tap from any page (not just the login page)
  useGoogleOneTapLogin({
    onSuccess: (credentialResponse: CredentialResponse) => {
      if (credentialResponse.credential) {
        login(credentialResponse.credential).catch((err) => {
          console.error('One Tap login failed:', err);
        });
      }
    },
    onError: () => {
      console.error('Google One Tap failed');
    },
    disabled: !!user || E2E_LOGIN_ENABLED || kiosk, // Don't show One Tap if already logged in, in E2E mode, or running as a kiosk
  });

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated: !!user,
        login,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
